import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PhoneArrowUpRightIcon, ChatBubbleLeftRightIcon, CalendarDaysIcon, EnvelopeIcon,
  DocumentChartBarIcon, MicrophoneIcon,
} from '@heroicons/react/24/outline';
import CallMonitorSection from './CallMonitorSection';
import TeamsSection from './TeamsSection';
import CalendarSection from './CalendarSection';
import EmailSection from './EmailSection';
import HRReportsSection from './HRReportsSection';
import VoiceEmailSection from './VoiceEmailSection';

const SECTIONS = [
  { key: 'call_monitor', label: 'Call Monitor', icon: PhoneArrowUpRightIcon, Comp: CallMonitorSection },
  { key: 'teams',        label: 'Teams',        icon: ChatBubbleLeftRightIcon, Comp: TeamsSection },
  { key: 'calendar',     label: 'Calendar',     icon: CalendarDaysIcon, Comp: CalendarSection },
  { key: 'email',        label: 'Email',        icon: EnvelopeIcon, Comp: EmailSection },
  { key: 'hr_reports',   label: 'HR Reports',   icon: DocumentChartBarIcon, Comp: HRReportsSection },
  { key: 'voice_email',  label: 'Voice Email',  icon: MicrophoneIcon, Comp: VoiceEmailSection },
];

// OAuth error codes surfaced via the redirect query string.
const OAUTH_ERRORS = {
  calendar_access_denied:         'Calendar connection cancelled — access was denied.',
  calendar_no_code:               'Google OAuth failed — no authorization code received.',
  calendar_invalid_state:         'Google OAuth failed — session expired. Please try connecting again.',
  calendar_token_exchange_failed: 'Google OAuth failed — could not exchange the authorization code.',
  calendar_save_failed:           'Connected, but credentials could not be saved. Please try again.',
  calendar_unexpected_error:      'An unexpected error occurred during Google Calendar connection.',
  gmail_access_denied:            'Gmail connection cancelled — access was denied.',
  gmail_no_code:                  'Gmail OAuth failed — no authorization code received.',
  gmail_invalid_state:            'Gmail OAuth failed — session expired. Please try connecting again.',
  gmail_token_exchange_failed:    'Gmail OAuth failed — could not exchange the authorization code.',
  gmail_save_failed:              'Gmail connected but credentials could not be saved. Please try again.',
  gmail_config_not_found:         'Gmail OAuth failed — email config not found.',
  gmail_unexpected_error:         'An unexpected error occurred during Gmail connection.',
};

function initialSection() {
  const params = new URLSearchParams(window.location.search);
  const sec = params.get('section');
  if (sec && SECTIONS.some((s) => s.key === sec)) return sec;
  // Infer from the OAuth provider if a section wasn't passed.
  const connected = params.get('connected');
  if (connected === 'gmail' || connected === 'outlook') return 'email';
  if (connected === 'google') return 'calendar';
  return 'call_monitor';
}

export default function IntegrationsTab() {
  const [section, setSection] = useState(initialSection);

  // Handle OAuth redirect feedback (?connected=… / ?error=…) then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) {
      toast.success(`${params.get('connected')} connected successfully!`);
    } else if (params.get('error')) {
      const code = params.get('error');
      toast.error(OAUTH_ERRORS[code] || `Connection failed: ${code}`, { duration: 6000 });
    }
    if (['connected', 'error', 'section', 'tab'].some((k) => params.get(k))) {
      window.history.replaceState({}, '', '/admin?tab=integrations');
    }
  }, []);

  const Active = SECTIONS.find((s) => s.key === section)?.Comp || CallMonitorSection;

  return (
    <div className="space-y-5">
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit overflow-x-auto">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                section === s.key ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      <Active />
    </div>
  );
}
